import React, { useCallback, useMemo, useState } from "react";
import { classNames } from "../../common_util";
import * as Model from "../types/cart";
import { ReactComponent as ArrowLeftIcon } from "images/icons/arrow-left.svg";
import { ReactComponent as AnimatedScooter } from "images/animated/animated_scooter.svg";
import { ReactComponent as PencilIcon } from "images/icons/pencil.svg"
import { ReactComponent as BinIcon } from "images/icons/bin-2.svg"
import { ReactComponent as ShoppingBagIcon } from "images/icons/shopping-bag-side.svg"
import { ReactComponent as VisaMark } from "images/payment-marks/visa.svg"
import { ReactComponent as MastercardMark } from "images/payment-marks/mastercard.svg"
import { ReactComponent as AfterpayMark } from "images/payment-marks/afterpay.svg"
import { ReactComponent as GooglePay } from "images/payment-marks/google-pay.svg";
import { ReactComponent as ApplePay } from "images/payment-marks/apple-pay.svg";
import { ReactComponent as LoadingSpinner } from "images/animated/loading_spinner.svg";
import { ReactComponent as TagFavorite } from "images/icons/tags-favorite.svg";
import { ReactComponent as SynchronizeArrowsSquare } from "images/icons/synchronize-arrows-square.svg";
import { ReactComponent as MoveExpandVerticalIcon } from "images/icons/move-expand-vertical.svg";
import { newLookReviewOrderPath } from "../../routes";
import { useStorefrontConfig, useStorefrontHelper } from "../types/storefront";
import { FulfillmentPopover } from "./fulfillment_popover";
import { Product } from "src/storefront/types/catalog";
import { buildTagElementsForTags } from "src/storefront/components/product_tags";
import { NavigatePage, StorefrontPage, useNavigationState } from "../types/navigation";

const Cart = ({
  cart,
  cartMaxHeightOffset,
  displayingCartAppView,
  storeHidesAgeRestrictedItems,
  editCartItem,
  removeCartItem,
  isLoading,
  addItemToCart
}: {
  cart: Model.Cart | null;
  cartMaxHeightOffset: number;
  isLoading: boolean;
  displayingCartAppView: boolean;
  storeHidesAgeRestrictedItems: boolean;
  editCartItem: (cartItem: Model.CartItem) => void;
  removeCartItem: (cartItem: Model.CartItem) => void;
  addItemToCart: (product: Product) => Promise<void>;
}) : React.JSX.Element => {
  const storefrontConfig = useStorefrontConfig();
  const [submitting, setSubmitting] = useState(false);
  const { catalog } = useStorefrontHelper();
  const [_navigationState, updateNavigation] = useNavigationState();

  const eligibleFreeProduct = useMemo(() => {
    const productKey = cart?.eligibleFreeProductKeys[0]
    return productKey ? catalog.findFirstAvailableProductByKeyFragment(storefrontConfig, productKey) : null;
  }, [catalog, cart?.eligibleFreeProductKeys, storefrontConfig]);

  const hideCart = useCallback(() => {
    updateNavigation(NavigatePage.action(StorefrontPage.Shop, storefrontConfig.usesNewBottomNav));
  }, [updateNavigation, storefrontConfig.usesNewBottomNav]);

  const onCheckoutClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (storefrontConfig.storeTypeRestrictions.vapeHealthWarning) {
      const container = document.getElementById("vapes-notice");
      if (container) {
        container.classList.add("is-active");
      }
    } else {
      checkout(e);
    }
  };

  if (!cart || cart.cartItemCount() <= 0) {
    return (
      <EmptyCart displayingCartAppView={displayingCartAppView} afterpayAllowed={storeHidesAgeRestrictedItems} />
    );
  }

  const cartProductSuggestion = () => {
    if (!eligibleFreeProduct) {
      return <></>;
    }

    return (
      <>
        <div className="container">
          <CartProductSuggestion
            onAddToCart={addItemToCart}
            product={eligibleFreeProduct}
          />
        </div>

        <div className="divider"></div>
      </>
    );
  };

  const checkout = (e : React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setSubmitting(true);
    window.location.href = newLookReviewOrderPath();
  }

  const formatter = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });
  const cartHeightOffsetDesktop = { "--cart-height-offset-desktop": cartMaxHeightOffset } as React.CSSProperties;

  const checkoutButtonClasses = classNames({
    "button": true,
    "is-primary": true,
    "is-full-width": true,
    "is-loading": isLoading || submitting,
  });

  const checkoutButtonIconClasses = classNames({
    "icon": true,
    "custom-icon": true,
    "is-medium": true,
    "is-completely-hidden": !submitting,
  });

  const belowMinimumSubtotal = (cart?.subtotal() ?? 0) > 0 && (cart?.subtotal() ?? 0) < (storefrontConfig.minimumSubtotal*100);
  const checkoutDisabled = cart?.cartItems ? (cart.cartItems.length == 0 || belowMinimumSubtotal) : true
  const checkoutButtonLabel = belowMinimumSubtotal ? `Minimum subtotal ${formatter.format(storefrontConfig.minimumSubtotal).replace('.00', '')}` : "Checkout"

  const showCheckoutButton = () => {
    if (isLoading) {
      return (
      <button className={checkoutButtonClasses} disabled={true}>
        <span className="icon custom-icon">
        <LoadingSpinner/>
        </span>
        <span>Updating...</span>
      </button>
      );
    }

    return (
      <button className={checkoutButtonClasses} disabled={checkoutDisabled} onClick={onCheckoutClick} data-cy="link-to-checkout">
        <span className={checkoutButtonIconClasses}>
          <AnimatedScooter/>
        </span>
        <span>{checkoutButtonLabel}</span>
      </button>
    );
  }

  const cartHeader = () => {
    if (storefrontConfig.usesNewBottomNav) {
      return (
        <FulfillmentPopover />
      );
    } else {
      return (
        <div className="nav has-title">
          <a className="button is-icon-only" onClick={hideCart}>
            <ArrowLeftIcon className="icon streamline-icon" />
          </a>

          <h1 className="is-medium-text flex-grow-1">Cart</h1>

          <FulfillmentPopover hasCompactTrigger />
        </div>
      );
    }
  }

  const substitutionsDisclaimer = <>
    <hr/>

    <div className="container" data-testid="substitutions-disclaimer">
      <div className="stack">
        <div className="is-flexbox align-items-center is-flex-gap-xs">
          <SynchronizeArrowsSquare className="icon streamline-icon has-fill-important"/>
          <h5><span>Substitutions</span></h5>
        </div>

        <div className="text-overflow-toggle"
             style={{ "--line-clamp": 2, "--collapse-label": "'Read less'" } as React.CSSProperties}>
          <input className="is-completely-hidden" name="expand_cart_substitutions_disclaimer" id="expand_cart_substitutions_disclaimer" type="checkbox"/>

          <p className="is-muted-text is-tiny-text space-mb-none">
            If a product is unavailable — the person packing your order will do their best to find a substitute with the
            same value or less.
            <br/><br/>
            Any substitution price difference will be refunded when the order is complete. If no substitutes are
            available then the full item value will be refunded. Refunds can take 5-7 business days to process.
          </p>

          <label htmlFor="expand_cart_substitutions_disclaimer" className="is-muted-text is-tiny-text">
            <MoveExpandVerticalIcon className="icon streamline-icon is-muted-text"/>
            <span>Read more</span>
          </label>
        </div>
      </div>
    </div>
  </>

  return (
    <div className={"app-view" + (displayingCartAppView && !storefrontConfig.usesNewBottomNav ? " is-active" : "")} id="cart" style={cartHeightOffsetDesktop}>
      {/* displayingCartAppView is-active logic can be removed here once all stores migrated to bottom navigation */}
        <header className="divider has-content has-high-contrast has-divider is-hidden-above-desktop">
          {cartHeader()}
        </header>

        <section className="scrollable-area has-space-top space-pb-none">
          {cartProductSuggestion()}

          <div className="container">
            <CartDisplay
              cart={cart}
              editCartItem={editCartItem}
              removeCartItem={removeCartItem}
            />
          </div>

          <div className="divider"></div>

          <div className="container">
            <div className="line-item">
              <span>Subtotal</span>
              <span>{formatter.format((cart?.subtotal() || 0) / 100)}</span>
            </div>

            <div className="line-item">
              <span>Delivery fee</span>
              <span data-testid="delivery-fee">{formatter.format((cart?.deliveryFee || 0) / 100)}</span>
            </div>

            <div className={classNames({ "line-item": true, "is-completely-hidden": (cart?.serviceFee || 0) <= 0 })}>
              <span>Service fee</span>
              <span data-testid="service-fee">{formatter.format((cart?.serviceFee || 0) / 100)}</span>
            </div>

            <div className={classNames({ "line-item": true, "is-completely-hidden": (cart?.subscriptionSavingsOnFees || 0) <= 0 })}>
              <span>🤙 Mates Rates</span>
              <span data-testid="subscription-delivery-fee-discount">{formatter.format((-cart?.subscriptionSavingsOnFees || 0) / 100)}</span>
            </div>

            <div className={classNames({ "line-item": true, "is-completely-hidden": (cart?.discount || 0) <= 0 })}>
              <span>Total discount</span>
              <span data-testid="discount">{formatter.format((-cart?.discount || 0) / 100)}</span>
            </div>

            <div className="line-item is-large">
              <span>Total</span>
              <span>{formatter.format((cart?.total() || 0) / 100)}</span>
            </div>
          </div>

          {storefrontConfig.showSubstitutionsDisclaimer ? substitutionsDisclaimer : null}

          <div className="is-stuck-bottom">
            <section className="has-gradient">
              <div className="container">
                {showCheckoutButton()}
              </div>
            </section>
          </div>
        </section>
    </div>
  );
}

const EmptyCart = ({ displayingCartAppView, afterpayAllowed }: { displayingCartAppView: boolean; afterpayAllowed: boolean }) => {
  const storefrontConfig = useStorefrontConfig();

  return (
    <div className={"app-view " + (displayingCartAppView && !storefrontConfig.usesNewBottomNav ? "is-visible-above-desktop" : "")} id="cart">
      <div className="card flex-grow-1">
        <section className="container is-flexbox flex-column align-items-center flex-grow-1 justify-content-center">
          <ShoppingBagIcon className="icon streamline-icon is-large has-fill-important space-mb-sm"/>
          <h5>Looking empty</h5>

          <p className="is-tiny-text is-muted-text is-center-aligned-text">Pop a few things in your cart to get started</p>

          <div className="is-flexbox is-flex-gap-md space-pt-sm">
            <VisaMark className="payment-mark responsive-image"/>
            <MastercardMark className="payment-mark responsive-image"/>
            <ApplePay className="payment-mark responsive-image" />
            <GooglePay className="payment-mark responsive-image" />
            {afterpayAllowed && <AfterpayMark className="payment-mark responsive-image" data-testid={"afterpay-mark"} />}
          </div>
        </section>
      </div>
    </div>
  );
}

const CartDisplay = ({
  cart,
  editCartItem,
  removeCartItem,
}: {
  cart: Model.Cart;
  editCartItem: (cartItem: Model.CartItem) => void;
  removeCartItem: (cartItem: Model.CartItem) => void;
}) : React.JSX.Element => {
  const cartItems = cart.cartItems.map((ci, index) => {
    const onEdit = () => editCartItem(ci);
    const onRemove = () => removeCartItem(ci);

    if (ci.quantity > 0) {
      return (
        <CartItemDisplay
          cartItem={ci}
          key={index}
          editCartItem={onEdit}
          removeCartItem={onRemove}
        />
      );
    }

  });

  return <>{cartItems}</>;
}

const CartItemDisplay = ({
  cartItem,
  editCartItem,
  removeCartItem,
}: {
  cartItem: Model.CartItem;
  editCartItem: () => void;
  removeCartItem: () => void;
}): React.JSX.Element => {
  const formatter = useMemo(() => {
    return new Intl.NumberFormat("en-NZ", {
      style: "currency",
      currency: "NZD",
    });
  }, [])

  const config = useStorefrontConfig();

  const extras = useMemo(() => cartItem.extrasFullDescription(),[cartItem])

  const extrasArrayMappedForPTags = extras.map((e, index) => {
    return <p className="is-tiny-text" key={index}>{e}</p>
  })

  const outOfStockResolutionPreference = useMemo(() => {
    if (!config.allowSubstitutionPreferences) return null;

    return (
      <div className="is-flexbox align-items-center is-flex-gap-xs substitution-preferences">
        <SynchronizeArrowsSquare className="icon streamline-icon has-fill-important"/>
        <p className="is-muted-text">{cartItem.outOfStuckResolutionPreference.title}</p>
      </div>
    );
  }, [cartItem.outOfStuckResolutionPreference.title, config.allowSubstitutionPreferences])

  const appliedDiscountBadge = useMemo(() => {
    if (!cartItem.isDiscounted()) {
      return null;
    }

    return (
      <div className="is-flexbox flex-column flex-grow-1 align-items-start">
      {
        Object.entries(cartItem.discounts).map(([couponName, discount]) => {
          return (
            <div key={couponName} className="badge is-important">
              <TagFavorite className="icon streamline-icon"/>
              {typeof discount == "number" ? <span>{couponName}</span> : <span>{discount.name}</span>}
            </div>
          );
        })
      }
      </div>
    );
  }, [cartItem])

  const priceDescriptor = useMemo(() => {
    const originalSubtotal = cartItem.subtotal();
    const discountedPrice = cartItem.discountedPrice()

    if (originalSubtotal != discountedPrice) {
      const descriptor = discountedPrice == 0 ? "FREE" : formatter.format(discountedPrice / 100.0)

      return (
        <div className="price">{descriptor} <s className="is-muted-text">{formatter.format(originalSubtotal / 100.0)}</s></div>
      );
    } else {
      return (
        <div className="price">{formatter.format((cartItem.subtotal()) / 100.0)}</div>
      );
    }
  }, [cartItem, formatter])

  return (
    <div className="line-item has-full-description" data-cy={"order-item"}>
      <div className="is-flexbox flex-grow-1 align-items-start">
        <div className="title">{cartItem.name}</div>
        <div className="badge is-quantity">{cartItem.quantity}</div>
      </div>

      <div className="is-flexbox flex-column is-flex-gap-xs flex-grow-1">
        <div className="is-flexbox flex-column flex-grow-1 align-items-start">
          {
            config.storefrontExperience.hideCartDescriptions || !cartItem.description ? <></> :
            <div className="description">
              <p className="is-tiny-text is-muted-text space-pb-sm">{cartItem.description}</p>
            </div>
          }

          {extrasArrayMappedForPTags.length !== 0 ? <div className="item-options">{extrasArrayMappedForPTags}</div> : <></>}

          {cartItem.mealPreferences ? <div className="meal-preference" data-testid="meal-preference"><p className="is-tiny-text is-muted-text">{cartItem.mealPreferences}</p></div> : <></>}
        </div>

        {appliedDiscountBadge}

        <div className="is-flexbox align-items-center">
          {priceDescriptor}

          <a className="chip is-action" onClick={editCartItem}>
            <PencilIcon className="icon streamline-icon" />
            <span>Edit</span>
          </a>

          <a className="chip is-action" onClick={removeCartItem}>
            <BinIcon className="icon streamline-icon" />
            <span>Delete</span>
          </a>
        </div>

        {outOfStockResolutionPreference}
      </div>
    </div>
  );
}

const CartProductSuggestion = ({ product, onAddToCart }: { product: Product; onAddToCart: (product: Product) => Promise<void> }): React.JSX.Element => {
  const formatter = new Intl.NumberFormat("en-NZ", {
    style: "currency",
    currency: "NZD",
  });

  const addToCart = useCallback(async () => {
    await onAddToCart(product);
  }, [onAddToCart, product]);

  const [primaryTags] = buildTagElementsForTags(product.tags);

  return (
    <div className="line-item has-full-description">
      <div className="title">
        <TagFavorite className="icon streamline-icon has-fill-important"/>
        &nbsp;
        {product.title}
      </div>
      <div className="description">
        <p className="is-tiny-text is-muted-text">{product.description}</p>
      </div>
      <div className="price">FREE <s className="is-muted-text">{formatter.format((product.price) / 100.0)}</s></div>
      {primaryTags}
      <button className={"button is-full-width"} onClick={addToCart} data-cy={"add-suggestion-to-cart-button"}>
        <span>Add to cart</span>
      </button>
    </div>
  );
}


export { Cart };
